import type { PageFilterState } from "./types";

/**
 * Format: `<operator>:<csv,of,values>` (e.g. `in:A,B`, `nin:rent`).
 * Returns null if the value is missing or unparsable.
 */
export function parseFilterParam(
  raw: string | null,
): { values: string[]; operator: string } | null {
  if (!raw) return null;
  const colonIdx = raw.indexOf(":");
  if (colonIdx === -1) return null;
  const operator = raw.slice(0, colonIdx);
  const csv = raw.slice(colonIdx + 1);
  const values = csv.split(",").map((v) => v.trim()).filter(Boolean);
  if (values.length === 0 || !operator) return null;
  return { values, operator };
}

export function serializeFilterParam(state: {
  values: string[] | null;
  operator: string;
}): string | null {
  if (!state.values || state.values.length === 0) return null;
  return `${state.operator}:${state.values.join(",")}`;
}

/**
 * Read all known filter keys from the current URL search params.
 * Returns a partial PageFilterState with only the keys present in the URL.
 */
export function readFiltersFromUrl(filterKeys: string[]): PageFilterState {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const result: PageFilterState = {};

  for (const key of filterKeys) {
    const parsed = parseFilterParam(params.get(key));
    if (parsed) {
      result[key] = { values: parsed.values, operator: parsed.operator };
    }
  }

  return result;
}

/**
 * Write the given page filters to the URL. Removes a filter's param when it
 * has no values. Uses replaceState so no history entries are added.
 *
 * Only writes the filter keys belonging to this page — leaves all other URL
 * params untouched (search, pagination, transaction modal id, etc.).
 */
export function writeFiltersToUrl(
  filterKeys: string[],
  pageFilters: PageFilterState,
): void {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);

  for (const key of filterKeys) {
    const state = pageFilters[key];
    const serialized = state ? serializeFilterParam(state) : null;
    if (serialized) {
      url.searchParams.set(key, serialized);
    } else {
      url.searchParams.delete(key);
    }
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) {
    window.history.replaceState(window.history.state, "", next);
  }
}
